import PdfViewer from "@/app/views/PdfViewer/PdfViewer.vue";
import { getIntFromQuery, type PastaRoute, PastaRouteType } from "@pasta/box-v2";
import { restrictedRoute, useAuthStore } from "@pasta/front-auth-v2";
import dayjs from "dayjs";
import { isNil } from "lodash";
import { BiPaperclip } from "oh-vue-icons/icons";
import { type RouteLocationNormalized } from "vue-router";
import { type LetterFileDto, type LetterFileSearchDto } from "./dto/letter-file.dto";
import Home from "./Home.vue";
import { joinLetterFileRoom } from "./router/join-letter-file-room.middleware";
import { loadAppContext } from "./router/load-app-context.middleware";
import { useLetterFileStore } from "./stores/letter-file.store";
import { useLoadingStore } from "./stores/loading.store";
import Contact from "./views/contact.vue";
import FluxError from "./views/FluxError.vue";
import LetterFileForm from "./views/LetterFileForm/LetterFileForm.vue";
import { getArrayStringFromQuery, getBoolFromQuery } from "./views/LetterFileForm/utils";
import LinkError from "./views/LinkError.vue";
import PersonnalData from "./views/personnal-data.vue";
import Promo from "./views/Promo.vue";
import SignHelp from "./views/SignHelp.vue";
import SiteMap from "./views/SiteMap.vue";
import UserGuide from "./views/UserGuide.vue";
import Versions from "./views/versions.vue";
import TransferLf from "./views/TransferLf.vue";

export const appRoutes: PastaRoute[] = [
  {
    name: "home",
    path: "/",
    component: Home,
    beforeEnter: [restrictedRoute, loadAppContext, () => joinLetterFileRoom(null)],
    menu: {
      label: "Accueil",
      icon: BiPaperclip,
    },
    props: (to: RouteLocationNormalized): LetterFileSearchDto & { page: number; pageSize: number } => {
      let fileTypes: string[] = [];
      if (!isNil(to.query.fileTypes)) {
        fileTypes = Array.isArray(to.query.fileTypes)
          ? (to.query.fileTypes as string[])
          : ([to.query.fileTypes] as string[]);
      }
      return {
        page: getIntFromQuery(to.query.page, 1),
        pageSize: getIntFromQuery(to.query.pageSize, 25),
        searchPattern: to.query.searchPattern as string,
        archived: !isNil(to.query.archived) ? getBoolFromQuery(to.query.archived) : null,
        tags: getArrayStringFromQuery(to.query.tags),
        alreadySeen: getBoolFromQuery(to.query.alreadySeen),
        createdByMe: getBoolFromQuery(to.query.createdByMe),
        inTodo: getBoolFromQuery(to.query.inTodo),
        inCC: getBoolFromQuery(to.query.inCC),
        inCurrent: getBoolFromQuery(to.query.inCurrent),
        inDone: getBoolFromQuery(to.query.inDone),
        startDate: to.query.startDate as string,
        endDate: (to.query.endDate as string) || dayjs().toISOString(),
        createdStartDate: to.query.createdStartDate as string,
        createdEndDate: (to.query.createdEndDate as string) || dayjs().toISOString(),
        fileTypes,
      };
    },
  },
  {
    name: "form-add",
    path: "/letter-file/",
    component: LetterFileForm,
    beforeEnter: [
      restrictedRoute,
      async (_to: RouteLocationNormalized) => {
        const loadingStore = useLoadingStore();
        loadingStore.setLoading("set-lf", true);
        const store = useLetterFileStore();
        const letterFile: LetterFileDto = await store.create({});
        store.setOne(letterFile);
        store.setCurrentId(letterFile.id);
        loadingStore.setLoading("set-lf", false);
        return `/letter-file/${letterFile.chronoNumber}`;
      },
    ],
  },
  {
    name: "form-update",
    path: "/letter-file/:chrono(\\d+)",
    component: LetterFileForm,
    beforeEnter: [restrictedRoute, loadAppContext],
  },
  {
    name: "doc-sign",
    path: "/letter-file/:chrono(\\d+)/document/:docId(\\d+)",
    component: PdfViewer,
    beforeEnter: [restrictedRoute, loadAppContext],
    type: PastaRouteType.ROOT,
  },
  {
    name: "sign-help",
    path: "/sign-help",
    component: SignHelp,
    beforeEnter: [restrictedRoute, loadAppContext],
  },
  {
    name: "fluxerror",
    path: "/fluxerror/",
    component: FluxError,
  },
  {
    name: "linkerror",
    path: "/linkerror/",
    component: LinkError,
  },
  {
    name: "promo",
    path: "/promo",
    component: Promo,
    type: PastaRouteType.ROOT,
  },
  {
    name: "contact",
    path: "/contact",
    component: Contact,
    beforeEnter: [restrictedRoute, loadAppContext, () => joinLetterFileRoom(null)],
  },
  {
    name: "user-guide",
    path: "/user-guide",
    component: UserGuide,
    beforeEnter: [restrictedRoute, loadAppContext, () => joinLetterFileRoom(null)],
  },
  {
    name: "site-map",
    path: "/site-map",
    component: SiteMap,
    beforeEnter: [restrictedRoute, loadAppContext, () => joinLetterFileRoom(null)],
  },
  {
    name: "personnal-data",
    path: "/personnal-data",
    component: PersonnalData,
    beforeEnter: [restrictedRoute, loadAppContext, () => joinLetterFileRoom(null)],
  },
  {
    name: "versions",
    path: "/versions",
    component: Versions,
    beforeEnter: [restrictedRoute, loadAppContext, () => joinLetterFileRoom(null)],
  },
  {
    name: "transfer-lf",
    path: "/transfer-lf",
    component: TransferLf,
    beforeEnter: [
      restrictedRoute,
      () => {
        if (useAuthStore().ability.cannot("transfer", "LetterFile")) {
          return { path: "/", name: "home" };
        }
      },
    ],
  },
];
